/**
 * Engagement & operations settings — WhatsApp re-engagement follow-ups,
 * media retention, and inbox notification defaults (crm_settings KV).
 */
import { useEffect, useState } from 'react';
import { BellRing, HardDrive, MessageCircleReply } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import {
  useEngagementSettings,
  usePatchEngagementSettings,
  type CrmEngagementSettings,
} from '@/api/bi/crmSettings';
import { notify } from '@/lib/toast';

export function CrmEngagementPage() {
  const q = useEngagementSettings();
  const patch = usePatchEngagementSettings();
  const [form, setForm] = useState<CrmEngagementSettings | null>(null);

  useEffect(() => {
    if (q.data) setForm(q.data);
  }, [q.data]);

  if (q.isLoading || !form) return <Skeleton className="h-96 w-full" />;
  if (q.isError) return <ErrorState title="Failed to load engagement settings" onRetry={() => q.refetch()} />;

  const re = form.reengagement;

  function setRe<K extends keyof CrmEngagementSettings['reengagement']>(
    key: K,
    value: CrmEngagementSettings['reengagement'][K],
  ) {
    setForm((f) => (f ? { ...f, reengagement: { ...f.reengagement, [key]: value } } : f));
  }

  async function save() {
    try {
      await patch.mutateAsync(form!);
      notify.success('Engagement settings saved');
    } catch {
      notify.error('Could not save settings');
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="Engagement & operations"
        description="Automated WhatsApp follow-ups for abandoned enquiries, attachment storage limits, and inbox notification defaults."
        actions={
          <Button onClick={() => void save()} disabled={patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Save all'}
          </Button>
        }
      />

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2 text-sm">
              <MessageCircleReply className="h-4 w-4" /> WhatsApp re-engagement
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Follows up on WhatsApp conversations the customer abandoned mid-enquiry (no tagged order, no
              ticket, no reply). Requires an approved Meta message template — the job stays dormant until a
              template name is set. Customers who reply "stop" are never contacted again.
            </p>
          </CardHeader>
          <CardBody className="grid gap-4 pt-0 md:grid-cols-2">
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <input type="checkbox" checked={re.enabled} onChange={(e) => setRe('enabled', e.target.checked)} />
              <span className="font-medium">Enable re-engagement follow-ups</span>
            </label>
            <Field label="Meta template name" hint="Exact name of the approved template in WhatsApp Manager, e.g. abandoned_enquiry_v1. Must have {{1}} for the customer's first name.">
              <Input value={re.template_name} placeholder="abandoned_enquiry_v1" onChange={(e) => setRe('template_name', e.target.value)} />
            </Field>
            <Field label="Template language code" hint="As registered with Meta, e.g. en_US.">
              <Input value={re.template_language} onChange={(e) => setRe('template_language', e.target.value)} />
            </Field>
            <Field label="Wait before follow-up (hours)" hint="How long after the customer's last message before we follow up.">
              <Input type="number" min={1} value={re.delay_hours} onChange={(e) => setRe('delay_hours', Number(e.target.value))} />
            </Field>
            <Field label="Give up after (days)" hint="Conversations older than this are never followed up.">
              <Input type="number" min={1} value={re.max_window_days} onChange={(e) => setRe('max_window_days', Number(e.target.value))} />
            </Field>
            <Field label="Quiet hours start" hint="Nepal time. No follow-ups are sent between start and end.">
              <Input value={re.quiet_hours_start} placeholder="21:00" onChange={(e) => setRe('quiet_hours_start', e.target.value)} />
            </Field>
            <Field label="Quiet hours end">
              <Input value={re.quiet_hours_end} placeholder="09:00" onChange={(e) => setRe('quiet_hours_end', e.target.value)} />
            </Field>
            <Field label="Max follow-ups per customer per month" hint="Hard cap to avoid pestering.">
              <Input type="number" min={1} value={re.max_per_contact_per_month} onChange={(e) => setRe('max_per_contact_per_month', Number(e.target.value))} />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2 text-sm">
              <HardDrive className="h-4 w-4" /> Media storage
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Inbound photos, screenshots, and voice notes are stored on the server so they render in the inbox
              and can be analysed by the AI.
            </p>
          </CardHeader>
          <CardBody className="grid gap-4 pt-0 md:grid-cols-2">
            <Field label="Retention (days)" hint="Files older than this are deleted nightly. Message text is kept forever.">
              <Input
                type="number" min={1}
                value={form.media.retention_days}
                onChange={(e) => setForm({ ...form, media: { ...form.media, retention_days: Number(e.target.value) } })}
              />
            </Field>
            <Field label="Max download size (MB)" hint="Attachments larger than this are skipped (a link is kept where available).">
              <Input
                type="number" min={1}
                value={form.media.max_download_mb}
                onChange={(e) => setForm({ ...form, media: { ...form.media, max_download_mb: Number(e.target.value) } })}
              />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2 text-sm">
              <BellRing className="h-4 w-4" /> Inbox notifications
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Defaults for agents in the conversations inbox (each browser still asks for permission).
            </p>
          </CardHeader>
          <CardBody className="grid gap-4 pt-0 md:grid-cols-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.notifications.desktop_enabled}
                onChange={(e) =>
                  setForm({ ...form, notifications: { ...form.notifications, desktop_enabled: e.target.checked } })
                }
              />
              Desktop notifications for new customer messages
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.notifications.sound_enabled}
                onChange={(e) =>
                  setForm({ ...form, notifications: { ...form.notifications, sound_enabled: e.target.checked } })
                }
              />
              Notification sound
            </label>
          </CardBody>
        </Card>

        <div className="flex justify-end pb-6">
          <Button onClick={() => void save()} disabled={patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Save all'}
          </Button>
        </div>
      </div>
    </>
  );
}

